import { ArrowUpRight, ChevronDown, X, Search, ChevronsUpDown, ArrowUpDown, Star } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { setShowBackToDashboardButton, setSelectedReceiver } from '../../store/reducers';
import { useState, useEffect, useMemo } from 'react';
import { useLanguage, getReviewReceiverDetailsTranslation } from '../../hooks/useLanguage';
import AiIcon from '../../assets/ai-icon.png';

export const ReviewReceiverDetails = ({ contacts, navigate, declarationIndex = 0 }) => {
  const dispatch = useDispatch();
  const selectedSenders = useSelector(state => state.user.selectedSender || []);
  const selectedReceivers = useSelector(state => state.user.selectedReceiver || []);
  const selectedAgents = useSelector(state => state.user.selectedAgent || []);
  const selectedSender = selectedSenders[declarationIndex] || null;
  const selectedReceiver = selectedReceivers[declarationIndex] || null;
  const selectedAgent = selectedAgents[declarationIndex] || null;
  const suggestions = useSelector(state => {
    const suggestionsState = state.suggestions;
    const suggestionsArray = suggestionsState?.suggestions;
    
    return Array.isArray(suggestionsArray) ? suggestionsArray : [];
  });
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name-asc');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isSpanish] = useLanguage();

  // Debug: Log suggestions changes
  useEffect(() => {
    console.log('ReviewReceiverDetails - suggestions changed:', {
      suggestions,
      count: Array.isArray(suggestions) ? suggestions.length : 'not an array',
      declarationIndex
    });
  }, [suggestions, declarationIndex]);

  // Get suggestions for current declaration index
  // Validate suggestions array before using find
  const currentSuggestions = Array.isArray(suggestions) && suggestions.length > 0
    ? suggestions.find(s => s !== null && s !== undefined && typeof s === 'object' && s.exportacion_index === declarationIndex)
    : null;
  const suggestedReceivers = (currentSuggestions?.receivers && Array.isArray(currentSuggestions.receivers)) 
    ? currentSuggestions.receivers 
    : [];
  

  // Match suggested receivers to contacts by id, nif, identificacion_fiscal, or name
  const matchedSuggestedReceivers = suggestedReceivers
    .map(suggestion => {
      const matchedContact = contacts.find(contact => 
        contact.id === suggestion.id ||
        (contact.nif && suggestion.nif && contact.nif === suggestion.nif) ||
        (contact.identificacion_fiscal && suggestion.identificacion_fiscal && contact.identificacion_fiscal === suggestion.identificacion_fiscal) ||
        (contact.nombre && suggestion.nombre && contact.nombre.toLowerCase() === suggestion.nombre.toLowerCase())
      );
      return matchedContact ? { ...matchedContact, score: suggestion.score, reason: suggestion.reason } : null;
    })
    .filter(Boolean)
    .sort((a, b) => (b.score || 0) - (a.score || 0)) // Sort by score descending
    .slice(0, 3); // Limit to top 3

  const sortOptions = [
    { value: 'name-asc', label: getReviewReceiverDetailsTranslation('nameAZ', isSpanish) },
    { value: 'name-desc', label: getReviewReceiverDetailsTranslation('nameZA', isSpanish) },
    { value: 'date-desc', label: getReviewReceiverDetailsTranslation('newestFirst', isSpanish) },
    { value: 'date-asc', label: getReviewReceiverDetailsTranslation('oldestFirst', isSpanish) }
  ];

  const handleReceiverSelect = (receiver) => {
    dispatch(setSelectedReceiver(receiver, declarationIndex));
    setIsModalOpen(false);
    setSearchQuery('');
  };

  // Filter contacts by search query across multiple fields
  const filteredContacts = useMemo(() => {
    if (!searchQuery || typeof searchQuery !== 'string') {
      return contacts;
    }
    
    const query = searchQuery.toLowerCase().trim();
    if (!query) {
      return contacts;
    }
    
    const filtered = contacts.filter(contact => {
      if (!contact || typeof contact !== 'object') {
        return false;
      }
      
      // Helper function to safely convert and search a field
      const searchInField = (field) => {
        if (field == null) return false;
        if (typeof field === 'string' && field.trim() === '') return false;
        if (typeof field === 'number') {
          return String(field).toLowerCase().includes(query);
        }
        const fieldStr = String(field).toLowerCase().trim();
        return fieldStr.length > 0 && fieldStr.includes(query);
      };
      
      // Search across multiple fields
      return searchInField(contact.nombre) ||
             searchInField(contact.identificacion_fiscal) ||
             searchInField(contact.nif) ||
             searchInField(contact.eori) ||
             searchInField(contact.direccion) ||
             searchInField(contact.localidad) ||
             searchInField(contact.pais);
    });
    
    return filtered;
  }, [contacts, searchQuery]);

  // Sort filtered contacts
  const sortedContacts = useMemo(() => {
    return [...filteredContacts].sort((a, b) => {
    switch (sortBy) {
      case 'name-asc':
          return (a.nombre || '').localeCompare(b.nombre || '');
      case 'name-desc':
          return (b.nombre || '').localeCompare(a.nombre || '');
      case 'date-asc':
          return new Date(a.date_created || 0) - new Date(b.date_created || 0);
      case 'date-desc':
          return new Date(b.date_created || 0) - new Date(a.date_created || 0);
      default:
        return 0;
    }
  });
  }, [filteredContacts, sortBy]);

  const getSortLabel = () => {
    return sortOptions.find(option => option.value === sortBy)?.label || getReviewReceiverDetailsTranslation('sortBy', isSpanish);
  };

  return (
    <div className="text-left text-sm flex flex-col" id='review-receiver'>
      <div className='flex justify-between gap-4 mb-2 items-center'>
        <h1 className='font-semibold'>{getReviewReceiverDetailsTranslation('receiver', isSpanish)}</h1>
      </div>

      <div className={`p-4 border-container rounded-xl form ${selectedSender && selectedReceiver ? "flex-1" : ""}`}>
        <div className='relative w-full'>
          <button 
            className={`w-full flex gap-2 justify-between items-center ${selectedReceiver ? "border-container rounded-md p-4 " : ""}`} 
            onClick={() => setIsModalOpen(true)}
          >
            {selectedReceiver ? <p className='truncate'>{selectedReceiver.nombre}</p> : <p className='opacity-75 truncate'>{getReviewReceiverDetailsTranslation('selectReceiver', isSpanish)}</p>}
            <ChevronsUpDown className="w-4 h-4" />
          </button>
        </div>
        {selectedReceiver && (
          <>
            <div className='review-flex flex gap-4 mt-4'>
              <div className="form-group flex-1" id={`receiver_nombre${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                <label htmlFor="companyName">{getReviewReceiverDetailsTranslation('receiverName', isSpanish)}</label>
                <p className='font-bold'>{selectedReceiver && selectedReceiver.nombre || "N/A"}</p>
              </div>
              <div className="form-group flex-1" id={`receiver_nif${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                <label htmlFor="address">{getReviewReceiverDetailsTranslation('receiverTaxId', isSpanish)}</label>
                <p className='font-bold'>{selectedReceiver && selectedReceiver.identificacion_fiscal || "N/A"}</p>
              </div>
            </div>
            <div className='review-flex flex gap-4'>
              <div className="form-group flex-1" id={`receiver_eori${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                <label htmlFor="taxId">{getReviewReceiverDetailsTranslation('receiverEori', isSpanish)}</label>
                <p className='font-bold'>{selectedReceiver && selectedReceiver.eori || "N/A"}</p>
              </div>
              <div className="form-group flex-1" id={`receiver_direccion${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                <label htmlFor="eori">{getReviewReceiverDetailsTranslation('receiverAddress', isSpanish)}</label>
                <p className='font-bold'>{selectedReceiver && selectedReceiver.direccion || "N/A"}</p>
              </div>
            </div>
            <div className='review-flex flex gap-4'>
              <div className="form-group flex-1" id={`receiver_localidad${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                <label htmlFor="city">{getReviewReceiverDetailsTranslation('receiverCity', isSpanish)}</label>
                <p className='font-bold'>{selectedReceiver && selectedReceiver.localidad || "N/A"}</p>
              </div>
              <div className="form-group flex-1" id={`receiver_pais${declarationIndex === 0 ? '' : `_${declarationIndex + 1}`}`}>
                <label htmlFor="postalCode">{getReviewReceiverDetailsTranslation('receiverCountry', isSpanish)}</label>
                <p className='font-bold'>{selectedReceiver && selectedReceiver.pais || "N/A"}</p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Suggested Receivers */}
      {!selectedReceiver && matchedSuggestedReceivers.length > 0 && (
        <>
        <h2 className="text-xs font-light mt-4">{getReviewReceiverDetailsTranslation('suggestedReceivers', isSpanish)}</h2>
        <div className="flex flex-wrap gap-2 mt-2">
          {matchedSuggestedReceivers.map((suggestedReceiver, index) => (
            <button
              key={`suggested-receiver-${declarationIndex}-${suggestedReceiver.id || index}-${index}`}
              onClick={() => handleReceiverSelect(suggestedReceiver)}
              className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-full transition-colors"
              title={suggestedReceiver.reason || `Score: ${(suggestedReceiver.score * 100).toFixed(0)}%`}
            >
              <img src={AiIcon} alt="AI Icon" className="w-4 h-4" />
              <span className="font-medium text-xs truncate">{suggestedReceiver.nombre}</span>
              {suggestedReceiver.score && (
                <span className="text-[8px] text-blue-400 font-semibold ">
                  {(suggestedReceiver.score * 100).toFixed(0)}%
                </span>
              )}
            </button>
          ))}
        </div>
        </>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{getReviewReceiverDetailsTranslation('selectReceiver', isSpanish)}</h2>
              <button onClick={() => {
                setIsModalOpen(false);
                setSearchQuery('');
              }} className="p-1 hover:bg-gray-100 rounded-full">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="flex gap-2 mb-4">
              {/* Search Input */}
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-2 border-container rounded-full"
                  placeholder={getReviewReceiverDetailsTranslation('searchReceivers', isSpanish)}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {/* Custom Sort Dropdown */}
              <div className="relative button-no-hover-effect">
                <button
                  className="flex items-center gap-2 px-2 pr-0 py-2 rounded-full"
                  onClick={() => setIsSortOpen(!isSortOpen)}
                >
                    <ArrowUpDown className="w-4 h-4" />
                  <span>{getSortLabel()}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isSortOpen && (
                  <div className="overflow-hidden absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border-container py-1 z-999">
                    {sortOptions.map((option) => (
                      <button
                        key={option.value}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                          sortBy === option.value ? 'text-primary' : 'text-gray-700'
                        }`}
                        onClick={() => {
                          setSortBy(option.value);
                          setIsSortOpen(false);
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="max-h-[60vh] min-h-[60vh] overflow-y-auto">
              {contacts.length > 0 ? (
                <div className="space-y-0">
                  {sortedContacts.length > 0 ? (
                    sortedContacts.map((contact, index) => (
                      <div 
                        key={`receiver-${declarationIndex}-${contact.id || index}-${index}`} 
                        className='p-3 px-0 hover:bg-gray-100 cursor-pointer border-bottom-gray'
                        onClick={() => handleReceiverSelect(contact)}
                      >
                        <div className="flex justify-between items-start gap-2">
                          <div>
                            <p className="font-medium">{contact.nombre}</p>
                            <p className="text-sm text-gray-500">
                              {contact.pais}
                            </p>
                          </div>
                          {(contact.identificacion_fiscal) && (
                            <span className="text-xs bg-bgGray px-3 py-1 rounded-full">
                              {contact.identificacion_fiscal }
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      {getReviewReceiverDetailsTranslation('noReceiversFound', isSpanish)} "{searchQuery}"
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className='opacity-75 mb-4'>{getReviewReceiverDetailsTranslation('noReceiversAvailable', isSpanish)}</p>
                  <button 
                    className='flex gap-2 items-center justify-center mx-auto px-4 py-2 bg-default text-white rounded-full'
                    onClick={() => {
                      setIsModalOpen(false);
                      dispatch(setShowBackToDashboardButton(true));
                      navigate("/receiver-management");
                    }}
                  >
                    <p>{getReviewReceiverDetailsTranslation('addReceiver', isSpanish)}</p>
                    <ArrowUpRight className='w-5 h-5 stroke-white' />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}